import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Shield, AlertCircle, Eye, EyeOff } from "lucide-react";
import { sendResetOtp, resetPassword } from "../apis/auth";

export default function ForgetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialEmail = location.state?.email || "";

  const [email, setEmail] = useState(initialEmail);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const sendOtp = async () => {
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = { email };
      const response = await sendResetOtp(data);
      if (response.data.success) {
        toast.success("OTP sent to your email");
        setOtpSent(true);
      } else {
        setError(response.data.error || "Failed to send OTP");
        toast.error(response.data.error || "Failed to send OTP");
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to send OTP";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!otp) {
      toast.error("Please enter the OTP");
      return;
    }
    if (!newPassword || !confirmNewPassword) {
      toast.error("Please enter new password and confirm it");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await resetPassword({ email, otp, newPassword });
      if (response.data.success) {
        toast.success("Password reset successful. Please login.");
        navigate("/login");
      } else {
        setError(response.data.error || "Failed to reset password");
        toast.error(response.data.error || "Failed to reset password");
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to reset password";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-white text-gray-900">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-extrabold text-gray-900">Reset Your Password</h1>
          <p className="mt-2 text-gray-600 text-sm max-w-xs mx-auto">
            Enter your registered email to receive an OTP and reset your password.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 hover:shadow-2xl transition-shadow">
          <div className="h-2 bg-gradient-to-r from-[#048AAD] to-[#048AAD]/90 rounded-t-2xl"></div>

          <div className="p-7">
            {/* Error */}
            {error && (
              <div className="mb-5 flex items-center gap-2 bg-rose-50 border-l-4 border-rose-500 rounded-md p-3 text-rose-700 text-sm font-medium">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            {/* Security badge */}
            <div className="mb-6 flex justify-center">
              <div className="flex items-center gap-2 bg-[#01CC54]/10 text-[#01CC54] text-xs font-semibold px-4 py-1.5 rounded-full shadow-sm border border-[#01CC54]/20">
                <Shield size={16} className="text-[#01CC54]" />
                <span className="text-gray-900">Secure Password Reset</span>
              </div>
            </div>

            {/* Form */}
            {!otpSent ? (
              <>
                <input
                  name="email"
                  type="email"
                  placeholder="Enter your registered email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-lg placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900"
                />
                <button
                  onClick={sendOtp}
                  disabled={loading}
                  className="mt-6 w-full rounded-2xl bg-[#048AAD] py-4 text-lg font-bold text-white shadow-lg transition-transform hover:scale-105 hover:shadow-xl hover:bg-[#048AAD]/90 focus:outline-none focus:ring-2 focus:ring-[#048AAD]/40 disabled:opacity-50"
                >
                  {loading ? "Sending OTP..." : "Send OTP"}
                </button>
              </>
            ) : (
              <>
                <input
                  name="otp"
                  type="text"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                  className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-lg placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition mb-4 text-gray-900"
                />
                <div className="relative w-full mb-4">
                  <input
                    name="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 pr-12 text-lg placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowNewPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#01CC54]"
                    aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                  >
                    {showNewPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                  </button>
                </div>
                <div className="relative w-full mb-6">
                  <input
                    name="confirmNewPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm New Password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 pr-12 text-lg placeholder-gray-500 shadow-sm focus:bg-white focus:border-[#01CC54] focus:ring-2 focus:ring-[#01CC54]/20 transition text-gray-900"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#01CC54]"
                    aria-label={showConfirmPassword ? "Hide confirm new password" : "Show confirm new password"}
                  >
                    {showConfirmPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                  </button>
                </div>
                <button
                  onClick={handleResetPassword}
                  disabled={loading}
                  className="w-full rounded-2xl bg-[#048AAD] py-4 text-lg font-bold text-white shadow-lg transition-transform hover:scale-105 hover:shadow-xl hover:bg-[#048AAD]/90 focus:outline-none focus:ring-2 focus:ring-[#048AAD]/40 disabled:opacity-50"
                >
                  {loading ? "Resetting Password..." : "Reset Password"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Extra Info */}
        <div className="mt-6 text-center">
          <p className="text-gray-600 text-sm">
            Remembered your password?{" "}
            <button
              onClick={() => navigate("/login")}
              type="button"
              className="text-[#01CC54] font-semibold hover:text-[#01CC54]/80 hover:underline transition-colors"
            >
              Back to Login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
